import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Zugriffssicherung für Absolventen der Fachkraft-Ausbildung.
 *
 * Hintergrund: Absolventen (besonders Alt-Absolventen der alten Homepage) haben
 * teilweise KEINE `bundle_enrollments`-Zeile mehr, weil ihre Ausbildung dort
 * abgeschlossen wurde. Der Lernbereich „Fachkraft für Musik" ist aber ein
 * Training-Only-Bereich (tier_level = 'none') und verlangt eine explizite
 * Einschreibung — dadurch sahen Absolventen ein Schloss.
 *
 * Geschäftsregel: Absolventen behalten dauerhaft Zugriff auf ihre
 * Ausbildungsinhalte. Dieser Hook liefert dafür die Kennzeichnung.
 */

/** Kurs-ID, an der das 330h-Fachkraft-Zertifikat hängt. */
export const FACHKRAFT_CERT_COURSE_ID = "c4f08edb-8c93-4135-9c9d-0324562109d8";

/** Pflichtstunden eines echten Fachkraft-Zertifikats (grenzt es von 3h-Kurszertifikaten ab). */
export const FACHKRAFT_CERT_HOURS = 330;

/** Lernbereiche, die für Absolventen dauerhaft freigeschaltet bleiben. */
const GRADUATE_UNLOCKED_AREA_SLUGS = ["fachkraft-fuer-musik"];

/** Prüft, ob ein Lernbereich für Absolventen dauerhaft freigeschaltet ist. */
export function isGraduateUnlockedArea(slug?: string | null): boolean {
  if (!slug) return false;
  return GRADUATE_UNLOCKED_AREA_SLUGS.includes(slug);
}

/**
 * Kombinierte Absolventen-Prüfung: entweder 330h-Zertifikat vorhanden
 * oder als Alt-Absolvent der alten Homepage markiert.
 *
 * `aktiv: false` fragt nichts ab (der gewohnte Kursplayer fragt nur, wenn es
 * zählt: Mitglied in einer Ausbildung ohne Buchung).
 */
export function useFachkraftGraduateAccess({ aktiv = true }: { aktiv?: boolean } = {}) {
  const { data: user } = useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.user ?? null;
    },
    staleTime: 10 * 60 * 1000,
  });

  const { data, isLoading } = useQuery({
    queryKey: ["fachkraft-graduate-access", user?.id],
    queryFn: async () => {
      if (!user?.id) return false;

      const [certResult, profileResult] = await Promise.all([
        supabase
          .from("certificates")
          .select("id")
          .eq("user_id", user.id)
          .eq("course_id", FACHKRAFT_CERT_COURSE_ID)
          .eq("hours", FACHKRAFT_CERT_HOURS)
          .maybeSingle(),
        supabase
          .from("profiles")
          .select("legacy_homepage_training")
          .eq("user_id", user.id)
          .maybeSingle(),
      ]);

      return !!certResult.data || profileResult.data?.legacy_homepage_training === true;
    },
    enabled: !!user?.id && aktiv,
    staleTime: 10 * 60 * 1000,
  });

  return {
    isFachkraftGraduate: data === true,
    isLoading: isLoading && !!user?.id && aktiv,
  };
}
